/**
 * Modulo per la gestione del form Contatti
 * Gestisce tutti i form di contatto (contatti, consulenza, promozione, chisiamo, faq, news)
 * con generazione dinamica del reCAPTCHA basata sul campo action
 */

import Page from "~/pages/Page";
import { getApi } from "~/auth";
import UI from "~/ui";
import $ from "jquery";
import { generaRecaptcha, recaptchaReady, recaptchaToken } from "~/recaptcha";
import { subscribeNewsletterWithConfirm, unsubscribeNewsletter } from "~/mailup";
import { ConsensiHelper } from "~/services/ConsensiHelper";
import { initSwiperCarousel } from "~/components/SwiperCarousel";
import { Fancybox } from "@fancyapps/ui";

/**
 * Interfaccia per la risposta della chiamata API
 */
interface ApiResponse {
    success: boolean;
    responseText?: string;
    message?: string;
}

interface Consenso {
    id: number;
    codice: string;
    consenso: boolean;
    obbligatorio: boolean;
    scaduto: boolean;
}

/**
 * Classe per la gestione della pagina Contatti
 */
class Contatti extends Page {

    private theForm: HTMLFormElement | null = null;
    private auth: any = null;
    private userInfo: any = null;
    private isUserAuthenticated: boolean = false;
    private isSubmitting: boolean = false;
    private recaptchaGenerated: boolean = false;
    private actionType: string = "contatti"; // Valore di default

    constructor() {
        super("contatti", "Contatti");
    }

    /**
     * Inizializza la pagina
     */
    pageStart(): Promise<void> {
        this.auth = getApi();
        this.userInfo = this.auth.getCached("userInfo");
        this.isUserAuthenticated = this.userInfo !== null && this.userInfo !== undefined && this.userInfo.anagraficaId > 0;

        // Il form può avere diversi ID in base alla pagina
        this.theForm = this.findContactForm();

        console.log("Form ID:", this.theForm?.id);

        if (!this.theForm) {
            console.warn("Form contatti non trovato");
            return Promise.resolve();
        }

        // ✅ Leggi il valore del campo action per il reCAPTCHA
        this.actionType = $("#action").val() as string || "contatti";
        console.info(`Form contatti inizializzato con action: ${this.actionType}`);

        // Inizializza il form con i dati dell'utente se loggato
        this.initFormContatti();

        // Gestisce la submit del form
        this.initFormSubmit();

        // Genera il reCAPTCHA con l'action corretta
        this.generateRecaptcha();

        // Inizializza lo Swiper della pagina Consulenza o Promozione (no-op sulle altre pagine)
        initSwiperCarousel('.swiperConsulenza', { base: 1, bp480: 1, bp576: 1, bp768: 2, bp992: 2, bp1200: 3, bp1400: 3, });
        initSwiperCarousel('.swiperPromozione', { base: 1, bp480: 1, bp576: 1, bp768: 2, bp992: 2, bp1200: 3, bp1400: 3, });

        // Inizializza Fancybox se presente nella pagina
        this.initFancybox();

        return Promise.resolve();
    }

    /**
     * Inizializza Fancybox su tutti gli elementi [data-fancybox] presenti nella pagina.
     * È un no-op se non ci sono elementi o se Fancybox non è caricato.
     */
    private initFancybox(): void {
        if (!document.querySelector('[data-fancybox]')) return;

        Fancybox.bind('[data-fancybox]');
    }

    /**
     * Trova il form di contatto nella pagina
     * Il form può avere diversi ID in base al contesto
     */
    private findContactForm(): HTMLFormElement | null {
        const formIds = [
            'form-contatti',
            'form-consulenza',
            'form-promozione',
            'form-chisiamo',
            'form-faq',
            'form-news'
        ];

        for (const formId of formIds) {
            const form = document.getElementById(formId) as HTMLFormElement;
            if (form) {
                return form;
            }
        }

        // Fallback: cerca per name attribute
        const formsByName = document.getElementsByName('form-contatti');
        return formsByName.length > 0 ? formsByName[0] as HTMLFormElement : null;
    }

    /**
 * ✅ Resetta completamente lo stato del form
 */
    private resetFormState(): void {
        if (!this.theForm) return;

        // ✅ Usa requestAnimationFrame per assicurarsi che il DOM sia pronto
        requestAnimationFrame(() => {
            if (!this.theForm) return;

            // ✅ APPROCCIO AGGRESSIVO: Rimuove tutte le classi di validazione
            this.theForm.classList.remove('was-validated', 'needs-validation');

            // ✅ Forza anche con jQuery
            $(this.theForm).removeClass('was-validated needs-validation');

            // ✅ Rimuove anche l'attributo novalidate temporaneamente e poi lo ri-aggiunge
            const hadNoValidate = this.theForm.hasAttribute('novalidate');
            this.theForm.removeAttribute('novalidate');

            // ✅ Rimuove tutte le classi di validazione dai singoli campi
            $(this.theForm).find("input, select, textarea").each(function () {
                const $element = $(this);

                // Rimuove le classi di stato
                $element.removeClass("is-invalid is-valid");

                // Rimuove attributi aria
                $element.removeAttr("aria-invalid aria-describedby");

                // Nasconde i feedback
                $element.siblings(".invalid-feedback").hide();
                $element.siblings(".valid-feedback").hide();
                $element.next(".invalid-feedback").hide();
                $element.next(".valid-feedback").hide();
            });

            // ✅ Nasconde tutti i messaggi nel form
            $(this.theForm).find(".invalid-feedback, .valid-feedback").hide();

            // ✅ Ripristina novalidate se c'era
            if (hadNoValidate) {
                this.theForm.setAttribute('novalidate', '');
            }

            console.info("✅ Stato del form resettato completamente");
        });
    }

    /**
     * ✅ Genera il reCAPTCHA con l'action dinamica dal campo hidden
     */
    private async generateRecaptcha(): Promise<void> {
        try {
            await recaptchaReady();

            // ✅ Usa il valore del campo #action per generare il token reCAPTCHA
            await generaRecaptcha(this.actionType, true);

            this.recaptchaGenerated = true;
            console.info(`reCAPTCHA generato per action: ${this.actionType}`);
        } catch (error) {
            console.error('Errore durante la generazione del reCAPTCHA:', error);
            this.recaptchaGenerated = false;
        }
    }

    /**
     * Inizializza il form con i dati dell'utente autenticato (se presente)
     */
    private initFormContatti(): void {
        if (this.isUserAuthenticated && this.userInfo) {
            // Precompila i campi con i dati dell'utente
            $("#nome-contatto").val(this.userInfo.nome || "");
            $("#cognome-contatto").val(this.userInfo.cognome || "");
            $("#email-contatto").val(this.userInfo.email || "");

            // ✅ Recupera il telefono o cellulare dall'array contatti
            const telefono = this.getTelefonoFromUserInfo();
            if (telefono) {
                $("#telefono-contatto").val(telefono);
            }

            // ✅ Imposta il checkbox privacy come checked e disabilitato
            $("#privacy-contatto")
                .prop("checked", true)
                .prop("disabled", true)
                .removeAttr("required");

            const hasNewsConsent = this.userInfo.consensi?.find((c: Consenso) => c.codice === 'NEWS')?.concesso ?? false;
            $("#newsletter-contatto").prop("checked", hasNewsConsent);
            const hasTerziConsent = this.userInfo.consensi?.find((c: Consenso) => c.codice === 'TERZI')?.concesso ?? false;
            $("#societapartner-contatto").prop("checked", hasTerziConsent);

            console.info("Utente autenticato:", this.userInfo.nome, this.userInfo.cognome);
        } else {

            // Aggiungi il required al checkbox privacy per utenti non autenticati
            const $chkPrivacy = $("#privacy-contatto");
            if (!$chkPrivacy.attr("required")) {
                $chkPrivacy
                    .prop("required", true)
                    .prop("disabled", false)
                    .prop("checked", false);
            }
        }
    }

    /**
     * ✅ Recupera il numero di telefono o cellulare dall'array contatti dell'utente
     * Priorità: Cellulare (CEL) > Telefono (TEL)
     */
    private getTelefonoFromUserInfo(): string | null {
        if (!this.userInfo || !this.userInfo.contatti || !Array.isArray(this.userInfo.contatti)) {
            return null;
        }

        // Cerca prima un cellulare (CEL)
        const cellulare = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'CELL' && c.valore
        );

        if (cellulare) {
            return this.formatTelefono(cellulare);
        }

        // Se non c'è cellulare, cerca un telefono (TEL)
        const telefono = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'TEL' && c.valore
        );

        if (telefono) {
            return this.formatTelefono(telefono);
        }

        return null;
    }

    /**
     * ✅ Formatta il numero di telefono con eventuale prefisso
     */
    private formatTelefono(contatto: any): string {
        if (!contatto || !contatto.valore) {
            return '';
        }

        // Se c'è un prefisso, lo concatena
        if (contatto.prefisso && contatto.prefisso.trim() !== '') {
            return `${contatto.prefisso} ${contatto.valore}`;
        }

        return contatto.valore;
    }

    /**
     * Inizializza la gestione della submit del form
     */
    private initFormSubmit(): void {
        if (!this.theForm) return;

        this.theForm.addEventListener('submit', async (event: Event) => {
            event.preventDefault();
            event.stopPropagation();

            // Impedisce il doppio click
            if (this.isSubmitting) {
                console.log("Invio già in corso...");
                return;
            }

            if (this.theForm && this.theForm.checkValidity()) {
                await this.richiestaContatto();
            } else if (this.theForm) {
                this.theForm.classList.add('was-validated');
            }
        }, false);
    }

    /**
     * Invia la richiesta di contatto
     */
    private async richiestaContatto(): Promise<void> {
        if (!this.theForm) return;

        // ✅ Verifica il reCAPTCHA prima di procedere
        const token = recaptchaToken();
        if (!token) {
            UI.confirmOneButton("", "Completa la verifica reCAPTCHA");
            return;
        }

        // Imposta il flag per impedire doppi click
        this.isSubmitting = true;
        this.disableSubmitButton(true);

        // Serializza i dati del form
        const formData: Record<string, any> = {};
        $(this.theForm)
            .serializeArray()
            .forEach((field) => {
                formData[field.name] = field.value;
            });

        // ✅ Aggiungi manualmente il checkbox privacy se utente autenticato
        if (this.isUserAuthenticated) {
            formData["chkAutorizza"] = "true";
        }

        console.info("Invio form contatti:", formData);

        this.spinner.spin();

        try {
            const url = `${window.BaseUrlWebApi}Mailing/RichiestaContatto`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            const data: ApiResponse = await response.json();

            if (data.success) {
                //Aggiornamento consensi per utenti autenticati
                if (this.isUserAuthenticated) {
                    await ConsensiHelper.saveAndSyncConsensi(
                        this.theForm,
                        this.userInfo.email || ""
                    );
                } else {
                    // Se l'utente ha selezionato la newsletter, effettua l'iscrizione
                    if ($("#newsletter-contatto").prop('checked')) {
                        await subscribeNewsletterWithConfirm(
                            (window as any).ATP_ListID_Newsletter,
                            (window as any).ATP_GroupId_FormContatto,
                            formData["Email"] || "",
                            formData["Nome"] || "",
                            formData["Cognome"] || "",
                            formData["Telefono"] || "");
                    }
                }

                // Reset del form
                this.theForm.reset();

                // ✅ Se l'utente è loggato, ricarica i dati nel form
                if (this.isUserAuthenticated) {
                    this.auth.clearCached("userInfo");
                    this.userInfo = this.auth.getCached("userInfo");
                    this.initFormContatti();
                }

                // ✅ Resetta lo stato del form DOPO aver ricaricato i dati
                this.resetFormState();

                // Mostra conferma
                UI.confirmOneButton(
                    "Richiesta inviata",
                    "E-mail correttamente inviata, sarai ricontattato prima possibile"
                );

                // Rigenera il reCAPTCHA per il prossimo utilizzo
                await this.generateRecaptcha();
            }
            else {
                const errorMessage = data.responseText || data.message || "Errore sconosciuto";
                UI.confirmOneButton("", `Non è stato possibile inviare la E-mail (${errorMessage})`);

                // Rigenera il reCAPTCHA in caso di errore
                await this.generateRecaptcha();
            }

        } catch (error: any) {
            console.error("Errore durante l'invio della richiesta:", error);
            const errorMessage = error.message || "Si è verificato un errore durante l'invio";
            UI.confirmOneButton("", `Errore durante l'invio della richiesta: ${errorMessage}`);

            // Rigenera il reCAPTCHA in caso di errore
            await this.generateRecaptcha();
        } finally {
            this.spinner.stop();
            this.isSubmitting = false;
            this.disableSubmitButton(false);
        }
    }

    /**
     * Disabilita/abilita il pulsante di submit
     */
    private disableSubmitButton(disable: boolean): void {
        const $submitButton = $("#submitContatti");

        if (disable) {
            $submitButton.prop('disabled', true);
            const originalText = $submitButton.html();
            $submitButton.data('original-text', originalText);
            $submitButton.html('<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Invio in corso...');
        } else {
            $submitButton.prop('disabled', false);
            const originalText = $submitButton.data('original-text');
            if (originalText) {
                $submitButton.html(originalText);
            }
        }
    }
}

export default Contatti;
